/**
 * ExecutionHeadersService
 *
 * The eight-header set every Execution Studio call carries
 * (TONIC-PEX-IMPL-FE-001 Task 1):
 *
 *   x-tenant-id · x-user-id · x-bucket-name · authorization · x-auth-token
 *   x-division-id   nullable — passed through even when absent
 *   x-session-id    one per UI session
 *   x-gtid          one per business transaction
 *
 * The first five come from the existing client path (`getAuthHeaders` +
 * `ProjectHeadersService`) so the Studio sends exactly what the rest of the app
 * sends. This module adds the three new ones on top rather than editing
 * ProjectHeadersService, which every other feature depends on.
 *
 * `X-Division-Id` and `X-Organisation-Id` are optional, and the server derives
 * the real values itself. **If sent they must match the JWT claim or the request
 * is rejected with a 403**, so an empty or guessed value is worse than no header
 * at all — they are only forwarded when userData actually has them.
 *
 * `X-User-Id` is required: batch authorises against the project's
 * `permission.json` and needs `execute`, where the old single-run endpoint did
 * not check at all.
 *
 * @module shared/middleware/services/ExecutionHeadersService
 */

import { getAuthHeaders } from '../../services/auth/authHeaders';
import { getSessionId, nextGtid } from '../../services/session/sessionIdentity';
import { ProjectHeadersService } from './ProjectHeadersService';

/**
 * Build the full header set for one Execution Studio request.
 *
 * @param {Object}  userData        user context (tenant, division, bucket, id)
 * @param {Object}  [options]
 * @param {string}  [options.gtid]  reuse an existing GTID — pass this for every
 *                                  request belonging to the same user action.
 *                                  Omit only for a genuinely new transaction.
 * @param {Object}  [options.extra] additional headers (e.g. Idempotency-Key)
 * @returns {Promise<Object>} headers
 */
export const buildExecutionHeaders = async (userData, options = {}) => {
  const { gtid, extra = {}, ...projectOverrides } = options;

  const baseHeaders = await getAuthHeaders();
  const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData, projectOverrides);

  // An empty X-Division-Id is a claim mismatch, not a "no division" signal —
  // drop the header rather than send a value the JWT will not agree with.
  if (!headers['X-Division-Id']) delete headers['X-Division-Id'];
  if (!headers['X-Organisation-Id']) delete headers['X-Organisation-Id'];

  // Required by the batch endpoints; without it the project permission check 403s
  if (!headers['X-User-Id'] && userData?.userId) {
    headers['X-User-Id'] = userData.userId;
  }

  headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  headers['X-Session-Id'] = getSessionId();
  headers['X-Gtid'] = gtid || nextGtid();

  return { ...headers, ...extra };
};

/**
 * Mint the GTID for a user action, to be threaded through every request that
 * action triggers. A batch Run is one GTID for the whole batch.
 *
 * @returns {string}
 */
export const startTransaction = () => nextGtid();

const ExecutionHeadersService = { buildExecutionHeaders, startTransaction };

export default ExecutionHeadersService;
